// src/pages/Employees.js
import { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useAuthStore from '../store/authStore';

export default function Employees() {
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [profileData, setProfileData] = useState({
        name: '',
        contact_no: '',
        email: '',
        short_intro: '',
        designation: '',
        linkedin_url: '',
        department: '',
        blood_group: '',
        profile_pic: null,
    });
    const [editId, setEditId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const navigate = useNavigate();
    const { access, orgId } = useAuthStore();

    useEffect(() => {
        if (!access || !orgId) {
            alert('Access token or Org ID missing. Please log in again.');
            navigate('/');
            return;
        }

        fetchDepartments();
        fetchEmployees();
    }, [access, orgId]);

    const fetchDepartments = async () => {
        try {
            const res = await axios.get(`/api/departments/?organization=${orgId}`);
            setDepartments(res.data);
        } catch (err) {
            console.error('Fetch departments error:', err);
        }
    };

    const fetchEmployees = async () => {
        try {
            const res = await axios.get(`/api/employees/?organization=${orgId}`);
            setEmployees(res.data);
        } catch (err) {
            console.error('Fetch employees error:', err);
        }
    };

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        setProfileData((prev) => ({
            ...prev,
            [name]: files ? files[0] : value,
        }));
    };

    const handleSubmit = async () => {
        const payload = new FormData();

        for (const key in profileData) {
            const value = profileData[key];

            if (key === 'profile_pic' && value === null) continue;

            if (value instanceof File || (typeof value === 'string' && value.trim() !== '')) {
                payload.append(key, value);
            }
        }

        payload.append('organization', orgId);

        try {
            if (editId) {
                await axios.put(`/api/employees/${editId}/`, payload);
                toast.success('Employee updated');
            } else {
                await axios.post('/api/employees/', payload);
                toast.success('Employee added');
            }

            setProfileData({
                name: '',
                contact_no: '',
                email: '',
                short_intro: '',
                designation: '',
                linkedin_url: '',
                department: '',
                blood_group: '',
                profile_pic: null,
            });
            setEditId(null);
            setShowModal(false);
            fetchEmployees();
        } catch (err) {
            console.error('Submit error:', err.response?.data || err.message);
            toast.error('Error saving employee');
        }
    };

    // const handleEdit = (emp) => {
    //     const departmentId = typeof emp.department === 'object'
    //         ? String(emp.department?.id)
    //         : emp.department
    //             ? String(emp.department)
    //             : '';

    //     setProfileData({
    //         name: emp.name || '',
    //         contact_no: emp.contact_no || '',
    //         email: emp.email || '',
    //         short_intro: emp.short_intro || '',
    //         designation: emp.designation || '',
    //         linkedin_url: emp.linkedin_url || '',
    //         // department: departmentId,
    //         department: String(departmentId), // 🔥 THIS fixes modal prefill
    //         blood_group: emp.blood_group || '',
    //         profile_pic: null,
    //     });

    //     setEditId(emp.id);
    //     setShowModal(true);
    // };

    const handleEdit = (emp) => {
        setEditingEmployee(emp); // store employee
        setEditId(emp.id);
        setShowModal(true);
    };

    useEffect(() => {
        if (!editingEmployee || departments.length === 0) return;

        const emp = editingEmployee;

        let departmentId = '';

        if (typeof emp.department === 'object' && emp.department?.id) {
            departmentId = emp.department.id;
        } else if (!isNaN(emp.department)) {
            departmentId = emp.department;
        } else {
            const matchedDept = departments.find(d => d.name === emp.department);
            departmentId = matchedDept?.id || '';
        }

        setProfileData({
            name: emp.name || '',
            contact_no: emp.contact_no || '',
            email: emp.email || '',
            short_intro: emp.short_intro || '',
            designation: emp.designation || '',
            linkedin_url: emp.linkedin_url || '',
            department: String(departmentId), // ✅ NOW it works
            blood_group: emp.blood_group || '',
            profile_pic: null,
        });
    }, [editingEmployee, departments]);

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this employee?')) return;
        try {
            await axios.delete(`/api/employees/${id}/`);
            toast.warn('Employee deleted');
            fetchEmployees();
        } catch (err) {
            console.error('Delete error:', err);
            toast.error('Error deleting employee');
        }
    };

    const handleDepartmentChange = async (employeeId, newDeptId) => {
        try {
            const payload = new FormData();
            payload.append('department', newDeptId);
            payload.append('organization', orgId);

            await axios.patch(`/api/employees/${employeeId}/`, payload);
            toast.success('Department updated');
            fetchEmployees();
        } catch (err) {
            console.error('Department update error:', err);
            toast.error('Failed to update department');
        }
    };

    return (
        <div className="p-6">
            <h2 className="text-2xl font-bold mb-4">Manage Employees</h2>
            <button
                onClick={() => {
                    setProfileData({
                        name: '',
                        contact_no: '',
                        email: '',
                        short_intro: '',
                        designation: '',
                        linkedin_url: '',
                        department: '',
                        blood_group: '',
                        profile_pic: null,
                    });
                    setEditId(null);
                    setShowModal(true);
                }}
                className="mb-4 bg-green-500 text-white px-4 py-2 rounded"
            >
                Add Employee
            </button>

            <table className="min-w-full table-auto border">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="border px-4 py-2">#</th>
                        <th className="border px-4 py-2">Name</th>
                        <th className="border px-4 py-2">Email</th>
                        <th className="border px-4 py-2">Contact</th>
                        <th className="border px-4 py-2">Designation</th>
                        <th className="border px-4 py-2">Department</th>
                        <th className="border px-4 py-2">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {employees.map((emp, index) => (
                        <tr key={emp.id} className="text-center">
                            <td className="border px-4 py-2">{index + 1}</td>
                            <td className="border px-4 py-2">{emp.name}</td>
                            <td className="border px-4 py-2">{emp.email}</td>
                            <td className="border px-4 py-2">{emp.contact_no}</td>
                            <td className="border px-4 py-2">{emp.designation}</td>
                            {/* <td className="border px-4 py-2">{emp.department}</td> */}
                            <td className="border px-4 py-2">
                                <select
                                    className="border p-1 rounded w-full"
                                    value={(() => {
                                        // Case 1: department is object
                                        if (typeof emp.department === 'object' && emp.department?.id) {
                                            return String(emp.department.id);
                                        }

                                        // Case 2: department is ID
                                        if (!isNaN(emp.department)) {
                                            return String(emp.department);
                                        }

                                        // Case 3: department is NAME → find matching department ID
                                        const matchedDept = departments.find(
                                            (d) => d.name === emp.department
                                        );
                                        return matchedDept ? String(matchedDept.id) : '';
                                    })()}
                                    onChange={(e) => handleDepartmentChange(emp.id, e.target.value)}
                                >
                                    <option value="">Select Department</option>
                                    {departments.map((dept) => (
                                        <option key={dept.id} value={String(dept.id)}>
                                            {dept.name}
                                        </option>
                                    ))}
                                </select>
                            </td>
                            <td className="border px-4 py-2">
                                <button onClick={() => handleEdit(emp)} className="text-yellow-500 hover:text-yellow-700 mr-2">Edit</button>
                                <button onClick={() => handleDelete(emp.id)} className="text-red-500 hover:text-red-700">Delete</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {showModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded shadow-md w-full max-w-2xl overflow-y-auto max-h-[90vh]">
                        <h3 className="text-xl font-bold mb-4">{editId ? 'Edit Employee' : 'Add Employee'}</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block mb-1 font-medium">Name</label>
                                <input type="text" name="name" className="border p-2 w-full rounded" value={profileData.name} onChange={handleChange} />
                            </div>

                            <div>
                                <label className="block mb-1 font-medium">Contact Number</label>
                                <input type="text" name="contact_no" className="border p-2 w-full rounded" value={profileData.contact_no} onChange={handleChange} />
                            </div>

                            <div>
                                <label className="block mb-1 font-medium">Email</label>
                                <input type="email" name="email" className="border p-2 w-full rounded" value={profileData.email} onChange={handleChange} />
                            </div>

                            <div>
                                <label className="block mb-1 font-medium">Designation</label>
                                <input type="text" name="designation" className="border p-2 w-full rounded" value={profileData.designation} onChange={handleChange} />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block mb-1 font-medium">LinkedIn URL</label>
                                <input type="text" name="linkedin_url" className="border p-2 w-full rounded" value={profileData.linkedin_url} onChange={handleChange} />
                            </div>

                            <div>
                                <label className="block mb-1 font-medium">Blood Group</label>
                                <select name="blood_group" className="border p-2 w-full rounded" value={profileData.blood_group} onChange={handleChange}>
                                    <option value="">Select Blood Group</option>
                                    {[
                                        'A+',
                                        'A-',
                                        'B+',
                                        'B-',
                                        'O+',
                                        'O-',
                                        'AB+',
                                        'AB-',
                                        'A1B+',
                                        'A1B-',
                                        'A2B+',
                                        'A2B-',
                                        'A1+',
                                        'A1-',
                                        'A2+',
                                        'A2-',
                                    ].map((group) => (
                                        <option key={group} value={group}>
                                            {group}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block mb-1 font-medium">Department</label>
                                <select name="department" className="border p-2 w-full rounded" value={profileData.department} onChange={handleChange}>
                                    <option value="">Select Department</option>
                                    {departments.map((dept) => (
                                        <option key={dept.id} value={String(dept.id)}>{dept.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block mb-1 font-medium">Profile Picture</label>
                                <input type="file" name="profile_pic" className="border p-2 w-full rounded" onChange={handleChange} />
                                {editId && (() => {
                                    const current = employees.find(emp => emp.id === editId);
                                    const imageUrl = current?.profile_pic?.startsWith('http')
                                        ? current.profile_pic
                                        : `https://nfcadmin.mhcockpit.com${current?.profile_pic}`;
                                    return current?.profile_pic ? (
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-600">Current Photo:</p>
                                            <img src={imageUrl} alt="Profile" className="w-24 h-24 rounded object-cover border" />
                                        </div>
                                    ) : null;
                                })()}
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block mb-1 font-medium">Short Introduction</label>
                            <textarea name="short_intro" rows={3} className="border p-2 w-full rounded" value={profileData.short_intro} onChange={handleChange} />
                        </div>

                        <div className="flex justify-end gap-2 mt-4">
                            <button onClick={() => setShowModal(false)} className="bg-gray-300 px-4 py-2 rounded">Cancel</button>
                            <button onClick={handleSubmit} className="bg-blue-500 text-white px-4 py-2 rounded">{editId ? 'Update' : 'Add'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
